/**
 * Exhibit application state machine — owns sim, render params, transport, URL.
 * UI and renderer bind to this instead of a god `main.ts`.
 */

import {
  applyGuideChrome,
  DEFAULT_GUIDE_MODE_ID,
  getGuideMode,
} from "../help/guideModes";
import {
  getDefaultPresetId,
  getPreset,
  PRESET_HOTKEYS,
  type Preset,
} from "../presets";
import {
  clampPitch,
  pickProjected,
  unprojectScreenToWorld,
} from "../render/camera";
import { CompositeRenderer } from "../render/composite";
import type { Renderer } from "../render/types";
import {
  BUILTIN_SCRIPTS,
  ChoreographyPlayer,
  getChoreographyScript,
  simulationTarget,
  type ChoreographyScript,
} from "./choreography";
import { FrameRecorder, recorderSupported } from "./recorder";
import { resolveAudioDefaults } from "../audio/defaults";
import { AudioEngine } from "../audio/engine";
import {
  cloneAudioParams,
  defaultAudioParams,
  validateAudioParams,
  type AudioParams,
} from "../audio/params";
import {
  cloneRenderParams,
  cloneSimParams,
  type RenderParams,
  type SimParams,
} from "../sim/params";
import { Simulation } from "../sim/simulation";
import { applyControlPatch } from "./controlPatch";
import { getShareModal } from "../ui/shareModal";
import {
  decodeState,
  prepareShareUrl,
  writeUrl,
  type AppStateV1,
} from "../ui/urlState";
import { AdaptiveQuality } from "./adaptiveQuality";
import { DEFAULT_SPEED, normalizeSpeed } from "./playbackSpeed";
import {
  DEFAULT_POINTER_TOOL,
  getPointerTool,
  type PointerToolId,
} from "./pointerTool";
import {
  DEFAULT_SPAWN_MODE,
  getSpawnMode,
  SPAWN_MODES,
  spawnPolicyForMode,
  type SpawnModeId,
} from "./spawnModes";
import {
  formatStepAggregates,
  formatStepAggregatesFull,
  StepTimingStats,
} from "./stepTiming";

export type ExhibitListener = () => void;

/** Pointer drag > this many CSS px counts as orbit, not pick (DESIGNS-TRACK-3D). */
const ORBIT_DRAG_PX = 4;
const ORBIT_SENS = 0.005;
const WHEEL_DOLLY = 0.0015;

/** Brief status after a successful rewire (transport chip). */
export type ToolFlash = {
  kind: "friend" | "enemy" | "info";
  text: string;
  until: number;
};

/** Kiosk render overlay when entering clean (Y2 — no grid flags). */
function applyKioskRenderOverlay(
  r: RenderParams,
  reducedMotion: boolean,
): RenderParams {
  return {
    ...r,
    autoRotate: reducedMotion ? 0 : 0.35,
    cameraFov: r.cameraFov > 0 ? r.cameraFov : 35,
  };
}

/** Y3 extension: grid/gnomon on clean kiosk enter when dim=3. */
function applyKioskGridOverlay(r: RenderParams, dim: number): RenderParams {
  if (dim < 3) return r;
  return { ...r, showGrid: true, showGnomon: true };
}

function queryWantsKiosk(): boolean {
  if (typeof location === "undefined") return false;
  try {
    return new URLSearchParams(location.search).get("kiosk") === "1";
  } catch {
    return false;
  }
}

export class ExhibitApp {
  sim: Simulation;
  render: RenderParams;
  presetId: string;
  dirty = false;
  playing: boolean;
  /** Target sim steps per animation frame (may be fractional: 0.25…8). */
  stepsPerFrame = DEFAULT_SPEED;
  guideModeId: string = DEFAULT_GUIDE_MODE_ID;
  lastStepMs = 0;
  fps = 0;
  /** Stage click tool — inspect / rewire / spawn. Not URL-persisted. */
  pointerTool: PointerToolId = DEFAULT_POINTER_TOOL;
  /** Spawn coupling mode when tool === spawn. Not URL-persisted. */
  spawnModeId: SpawnModeId = DEFAULT_SPAWN_MODE;
  /** Ephemeral status after rewire / tool tips (ms wall clock). */
  toolFlash: ToolFlash | null = null;

  private readonly canvas: HTMLCanvasElement;
  private readonly renderer: Renderer;
  private readonly quality: AdaptiveQuality;
  private readonly stepStats = new StepTimingStats(120);
  private readonly recorder = new FrameRecorder();
  private readonly audioEngine = new AudioEngine();
  /** Sacred Lab/URL audio prefs (working copies only go through applyAudio). */
  private audioState: AudioParams = defaultAudioParams();
  /** Audio keys the user has patched this session (enable-edge heuristics). */
  private audioExplicit = new Set<string>();
  private stageUnlockBound = false;
  private choreo: ChoreographyPlayer | null = null;
  private readonly listeners = new Set<ExhibitListener>();
  private urlTimer: ReturnType<typeof setTimeout> | null = null;
  private raf = 0;
  private frames = 0;
  private fpsWindow = performance.now();
  private disposed = false;
  private tabVisible = true;
  /** Throttle DOM/HUD emits so controls aren't rewritten 60×/s */
  private lastHudEmit = 0;
  private needsEmit = false;
  /**
   * Fractional-speed accumulator: each frame adds `stepsPerFrame`;
   * when credit ≥ 1, take floor(credit) integer sim steps.
   */
  private stepCredit = 0;
  /** Wall-clock for auto_rotate integration */
  private lastFrameNow = 0;
  /**
   * When paused, skip RAF redraws until something changes.
   * Prevents adaptive-quality edge strobing on Frozen Graph (static + edges on).
   */
  private needsDraw = true;

  /** Active pointer orbit gesture (dim=3). */
  private orbit: {
    pointerId: number;
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
    yaw0: number;
    pitch0: number;
    moved: boolean;
    altKey: boolean;
    metaKey: boolean;
  } | null = null;

  private readonly reducedMotion: boolean;
  /** Snapshot for exiting kiosk (guide + render before enter). */
  private preKiosk: { guide: string; render: RenderParams } | null = null;

  constructor(
    canvas: HTMLCanvasElement,
    opts?: { reducedMotion?: boolean; adaptiveQuality?: boolean },
  ) {
    const reduced = opts?.reducedMotion ?? false;
    this.reducedMotion = reduced;
    this.quality = new AdaptiveQuality({
      enabled: opts?.adaptiveQuality !== false,
    });
    this.canvas = canvas;
    const fromUrl =
      typeof location !== "undefined" ? decodeState(location.hash) : null;
    const wantKiosk = queryWantsKiosk();

    let presetId = getDefaultPresetId();
    let params: SimParams;
    let render: RenderParams;
    let audio: AudioParams = defaultAudioParams();
    let playing = !reduced;
    let dirty = false;
    let stepsPerFrame = DEFAULT_SPEED;
    let guideModeId = DEFAULT_GUIDE_MODE_ID;

    if (fromUrl) {
      presetId = fromUrl.preset || presetId;
      params = cloneSimParams(fromUrl.params);
      render = cloneRenderParams(fromUrl.render);
      audio = cloneAudioParams(fromUrl.audio ?? defaultAudioParams());
      playing = fromUrl.playing;
      dirty = fromUrl.dirty;
      stepsPerFrame = normalizeSpeed(fromUrl.stepsPerFrame);
      guideModeId = getGuideMode(fromUrl.guide).id;
    } else {
      const preset = getPreset(presetId);
      params = cloneSimParams(preset.params);
      render = cloneRenderParams(preset.render);
      audio = cloneAudioParams(preset.audio ?? defaultAudioParams());
    }

    // Ephemeral selection never sticks from URL (decode already clears; belt+suspenders)
    render.selectedIndex = -1;

    this.presetId = presetId;
    this.render = render;
    // Boot: never unlock AudioContext from URL (autoplay policy)
    this.audioState = validateAudioParams(audio);
    this.playing = playing;
    this.dirty = dirty;
    this.stepsPerFrame = stepsPerFrame;
    this.guideModeId = guideModeId;
    this.sim = new Simulation(params);
    if (!fromUrl) this.sim.step(30);
    this.audioEngine.setPlaying(this.playing);
    this.bindStageAudioUnlock();

    // Composite: Canvas2D by default; WebGL points above n threshold (DESIGNS §6).
    this.renderer = new CompositeRenderer(canvas);

    // Kiosk boot (DESIGNS-TRACK-Y §Y2)
    if (guideModeId === "kiosk") {
      // Already kiosk from hash: chrome only, no overlay re-apply
      this.preKiosk = {
        guide: DEFAULT_GUIDE_MODE_ID,
        render: cloneRenderParams(render),
      };
      applyGuideChrome(getGuideMode("kiosk"));
    } else if (wantKiosk) {
      this.enterKiosk({ applyOverlay: !dirty });
    } else {
      applyGuideChrome(getGuideMode(guideModeId));
    }
  }

  /** Enter kiosk guide; optionally overlay auto-rotate / FOV (and grid after Y3). */
  enterKiosk(opts?: { applyOverlay?: boolean }): void {
    if (this.guideModeId === "kiosk" && this.preKiosk) {
      applyGuideChrome(getGuideMode("kiosk"));
      return;
    }
    this.preKiosk = {
      guide: this.guideModeId,
      render: cloneRenderParams(this.render),
    };
    this.guideModeId = "kiosk";
    if (opts?.applyOverlay !== false) {
      let next = applyKioskRenderOverlay(this.render, this.reducedMotion);
      next = applyKioskGridOverlay(next, this.sim.params.dim);
      this.render = next;
    } else if (this.reducedMotion) {
      this.render = { ...this.render, autoRotate: 0 };
    }
    applyGuideChrome(getGuideMode("kiosk"));
    this.scheduleUrl();
    this.emitSoon(true);
  }

  exitKiosk(): void {
    if (this.guideModeId !== "kiosk") return;
    if (this.preKiosk) {
      this.guideModeId = getGuideMode(this.preKiosk.guide).id;
      this.render = cloneRenderParams(this.preKiosk.render);
      this.preKiosk = null;
    } else {
      this.guideModeId = DEFAULT_GUIDE_MODE_ID;
    }
    applyGuideChrome(getGuideMode(this.guideModeId));
    this.scheduleUrl();
    this.emitSoon(true);
  }

  get recording(): boolean {
    return this.recorder.recording;
  }

  get canRecord(): boolean {
    return recorderSupported();
  }

  get params(): SimParams {
    return this.sim.params;
  }

  getPreset(): Preset {
    try {
      return getPreset(this.presetId);
    } catch {
      return getPreset(getDefaultPresetId());
    }
  }

  subscribe(fn: ExhibitListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  /** Immediate emit for discrete UI events; throttled for per-frame HUD. */
  private emitSoon(force = false): void {
    if (force) {
      // UI events often change what should be on screen while paused
      this.needsDraw = true;
      this.needsEmit = false;
      this.lastHudEmit = performance.now();
      this.emit();
      return;
    }
    this.needsEmit = true;
  }

  start(stageEl: Element): void {
    this.renderer.observe(stageEl);
    this.renderer.draw(this.sim, this.quality.apply(this.render));

    document.addEventListener("visibilitychange", this.onVisibility);
    window.addEventListener("keydown", this.onKey);
    this.canvas.addEventListener("pointerdown", this.onPointerDown);
    this.canvas.addEventListener("pointermove", this.onPointerMove);
    this.canvas.addEventListener("pointerup", this.onPointerUp);
    this.canvas.addEventListener("pointercancel", this.onPointerUp);
    this.canvas.addEventListener("wheel", this.onWheel, { passive: false });
    this.canvas.style.touchAction = "none";

    this.fpsWindow = performance.now();
    this.lastFrameNow = performance.now();
    const loop = (now: number) => {
      if (this.disposed) return;
      this.raf = requestAnimationFrame(loop);
      this.tickFrame(now);
    };
    this.raf = requestAnimationFrame(loop);
    this.scheduleUrl(true);
    this.emitSoon(true);
  }

  dispose(): void {
    this.disposed = true;
    this.audioEngine.dispose();
    cancelAnimationFrame(this.raf);
    this.renderer.disconnect();
    document.removeEventListener("visibilitychange", this.onVisibility);
    window.removeEventListener("keydown", this.onKey);
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
    this.canvas.removeEventListener("pointerup", this.onPointerUp);
    this.canvas.removeEventListener("pointercancel", this.onPointerUp);
    this.canvas.removeEventListener("wheel", this.onWheel);
    if (this.urlTimer) clearTimeout(this.urlTimer);
  }

  private canvasLocal(ev: { clientX: number; clientY: number }): {
    x: number;
    y: number;
  } {
    const rect = this.canvas.getBoundingClientRect();
    return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
  }

  private onPointerDown = (ev: PointerEvent): void => {
    if (ev.button !== 0) return;
    const { x, y } = this.canvasLocal(ev);
    if (this.sim.params.dim >= 3) {
      this.orbit = {
        pointerId: ev.pointerId,
        startX: x,
        startY: y,
        lastX: x,
        lastY: y,
        yaw0: this.render.cameraYaw,
        pitch0: this.render.cameraPitch,
        moved: false,
        altKey: ev.altKey,
        metaKey: ev.metaKey,
      };
      try {
        this.canvas.setPointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
    } else {
      // 2D: pick immediately on down (click path)
      this.handleStageClick(x, y, ev.altKey || ev.metaKey);
    }
  };

  private onPointerMove = (ev: PointerEvent): void => {
    const o = this.orbit;
    if (!o || o.pointerId !== ev.pointerId) return;
    const { x, y } = this.canvasLocal(ev);
    const dx = x - o.startX;
    const dy = y - o.startY;
    if (!o.moved && dx * dx + dy * dy > ORBIT_DRAG_PX * ORBIT_DRAG_PX) {
      o.moved = true;
    }
    if (!o.moved) return;
    o.lastX = x;
    o.lastY = y;
    const yaw = o.yaw0 + dx * ORBIT_SENS;
    const pitch = clampPitch(o.pitch0 + dy * ORBIT_SENS);
    this.render = { ...this.render, cameraYaw: yaw, cameraPitch: pitch };
    // Live orbit — no dirty flag (view-only); still URL-persist on up
    this.emitSoon(false);
  };

  private onPointerUp = (ev: PointerEvent): void => {
    const o = this.orbit;
    if (!o || o.pointerId !== ev.pointerId) return;
    const { x, y } = this.canvasLocal(ev);
    const wasOrbit = o.moved;
    const enemyMod = o.altKey || o.metaKey || ev.altKey || ev.metaKey;
    this.orbit = null;
    try {
      this.canvas.releasePointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
    if (wasOrbit) {
      this.dirty = true;
      this.scheduleUrl();
      this.emitSoon(true);
      return;
    }
    // Click without drag → tool action in projected screen space
    this.handleStageClick(x, y, enemyMod);
  };

  private onWheel = (ev: WheelEvent): void => {
    if (this.sim.params.dim < 3) return;
    ev.preventDefault();
    const factor = 1 + ev.deltaY * WHEEL_DOLLY;
    const next = Math.max(
      0.5,
      Math.min(20, this.render.cameraDistance * factor),
    );
    if (next === this.render.cameraDistance) return;
    this.render = { ...this.render, cameraDistance: next };
    this.dirty = true;
    this.scheduleUrl();
    this.emitSoon(true);
  };

  /** Hit-test nearest particle; -1 if none in range. */
  hitTestScreen(px: number, py: number): number {
    const sx = this.renderer.screenX;
    const sy = this.renderer.screenY;
    if (!sx || !sy || sx.length !== this.sim.n) {
      this.renderer.draw(this.sim, this.quality.apply(this.render));
    }
    const screenX = this.renderer.screenX;
    const screenY = this.renderer.screenY;
    if (!screenX || !screenY || screenX.length !== this.sim.n) return -1;
    // Generous radius: pointSize is often 1–2px; users expect ~finger/cursor target size.
    const hitR = Math.max(18, this.render.pointSize * 8);
    return pickProjected(screenX, screenY, this.sim.n, px, py, hitR);
  }

  /** Pick nearest particle using last-frame projected screen coords (inspect). */
  pickAtScreen(px: number, py: number): void {
    this.handleStageClick(px, py, false);
  }

  /**
   * Pause for aiming. Pick tools freeze motion so 120fps clouds are clickable.
   * Does not touch freezeTopology (graph rechoose) — only playback.
   */
  private pauseForTool(reason: string): void {
    if (!this.playing) return;
    this.playing = false;
    this.needsDraw = true; // one full-quality frame (edges stay on — no Q strobe)
    this.scheduleUrl();
    void reason;
  }

  /**
   * Stage click dispatched by tool.
   * @param enemyMod Alt/Option (or Meta) → enemy rewire on second click
   */
  handleStageClick(px: number, py: number, enemyMod: boolean): void {
    if (this.pointerTool === "spawn") {
      this.handleSpawnClick(px, py);
      return;
    }
    const hit = this.hitTestScreen(px, py);
    if (this.pointerTool === "rewire") {
      this.handleRewireClick(hit, enemyMod);
    } else {
      this.handleInspectClick(hit);
    }
  }

  private handleInspectClick(hit: number): void {
    const cur = this.render.selectedIndex ?? -1;
    let next: number;
    if (hit < 0) {
      if (cur < 0) {
        this.setToolFlash(
          "info",
          this.playing
            ? "Hard to aim while playing — click a particle (auto-pauses) or press Space first"
            : "Click a particle (white · blue friend · amber enemy)",
        );
        this.emitSoon(true);
        return;
      }
      next = -1;
      this.setToolFlash("info", "Selection cleared — Space to play");
    } else if (cur === hit) {
      next = -1;
      this.setToolFlash("info", "Selection cleared — Space to play");
    } else {
      next = hit;
      this.pauseForTool("inspect");
      const f = this.sim.friends[hit]!;
      const e = this.sim.enemies[hit]!;
      this.setToolFlash(
        "info",
        `Paused · #${hit} · friend #${f} · enemy #${e} · Space to play`,
      );
    }
    this.render = { ...this.render, selectedIndex: next };
    this.renderer.draw(this.sim, this.quality.apply(this.render));
    this.emitSoon(true);
  }

  private handleRewireClick(hit: number, enemyMod: boolean): void {
    const source = this.render.selectedIndex ?? -1;
    // Empty space → cancel pending source
    if (hit < 0) {
      if (source >= 0) {
        this.render = { ...this.render, selectedIndex: -1 };
        this.setToolFlash("info", "Rewire cancelled — Space to play");
        this.renderer.draw(this.sim, this.quality.apply(this.render));
        this.emitSoon(true);
      } else {
        this.setToolFlash(
          "info",
          this.playing
            ? "Rewire: click a source (auto-pauses) or Space to pause first"
            : "Rewire: click a source particle",
        );
        this.emitSoon(true);
      }
      return;
    }
    // No source yet → pick source
    if (source < 0) {
      this.pauseForTool("rewire");
      this.render = { ...this.render, selectedIndex: hit };
      this.setToolFlash(
        "info",
        enemyMod
          ? `Paused · source #${hit} — click target for enemy (amber)`
          : `Paused · source #${hit} — click target for friend (blue)`,
      );
      this.renderer.draw(this.sim, this.quality.apply(this.render));
      this.emitSoon(true);
      return;
    }
    // Same particle → clear (cancel)
    if (hit === source) {
      this.render = { ...this.render, selectedIndex: -1 };
      this.setToolFlash("info", "Rewire cancelled — Space to play");
      this.renderer.draw(this.sim, this.quality.apply(this.render));
      this.emitSoon(true);
      return;
    }
    // Second click → rewire (already paused from source pick; keep paused)
    this.pauseForTool("rewire");
    if (enemyMod) {
      this.sim.rewireEnemy(source, hit);
      this.setToolFlash("enemy", `enemy #${source} → #${hit} · Space to play`);
    } else {
      this.sim.rewireFriend(source, hit);
      this.setToolFlash(
        "friend",
        `friend #${source} → #${hit} · Space to play`,
      );
    }
    // Keep source selected so the new link is visible
    this.render = { ...this.render, selectedIndex: source };
    this.dirty = true;
    this.scheduleUrl();
    this.renderer.draw(this.sim, this.quality.apply(this.render));
    this.sonifyAfterWork({ forceExtract: true, fullQualityAudio: true });
    this.emitSoon(true);
  }

  private handleSpawnClick(px: number, py: number): void {
    // Click anywhere on stage → place a particle in world space at that pixel.
    const cssW = Math.max(1, this.canvas.clientWidth || 1);
    const cssH = Math.max(1, this.canvas.clientHeight || 1);
    const world = unprojectScreenToWorld(
      px,
      py,
      this.render,
      cssW,
      cssH,
      this.sim.dim,
    );
    const coords =
      this.sim.dim >= 3 ? [world.x, world.y, world.z] : [world.x, world.y];
    const mode = getSpawnMode(this.spawnModeId);
    const policy = spawnPolicyForMode(mode, this.sim.params.neighborRadius);
    const res = this.sim.addParticleAt(coords, policy);
    if (!res) {
      this.setToolFlash(
        "info",
        `Spawn cap ${Simulation.SPAWN_MAX_N} — lower n or Reset`,
      );
      this.emitSoon(true);
      return;
    }
    const { index: idx, meta } = res;
    // Spawn stays live — do not pause playback while painting particles
    this.render = { ...this.render, selectedIndex: idx };
    this.dirty = true;
    this.needsDraw = true;
    this.scheduleUrl();
    const inbound =
      meta.inbound.length > 0
        ? ` · inbound ${meta.inbound.map((e) => `${e.role[0]!.toUpperCase()}←#${e.agent}`).join(",")}`
        : "";
    const fb = meta.usedGlobalFallback ? " · global fallback" : "";
    this.setToolFlash(
      "friend",
      `+1 · #${idx} · F→#${meta.friend} · E→#${meta.enemy}${inbound}${fb} · ${mode.label}`,
      4500,
    );
    this.renderer.draw(this.sim, this.quality.apply(this.render));
    this.sonifyAfterWork({ forceExtract: true, fullQualityAudio: true });
    this.emitSoon(true);
  }

  setPointerTool(id: PointerToolId | string): void {
    const tool = getPointerTool(id);
    if (tool.id === this.pointerTool) return;
    this.pointerTool = tool.id;
    if (tool.id === "rewire") {
      // Clear stale selection so rewire always starts at "pick source"
      this.render = { ...this.render, selectedIndex: -1 };
      this.setToolFlash(
        "info",
        "Rewire: click source (pauses), then target · Alt=enemy",
      );
    } else if (tool.id === "spawn") {
      const mode = getSpawnMode(this.spawnModeId);
      this.setToolFlash(
        "friend",
        `Spawn · ${mode.label} · ${mode.hint} (stays live)`,
        6000,
      );
    } else {
      this.setToolFlash(
        "info",
        "Inspect: click a particle (pauses) · Space plays",
      );
    }
    this.canvas.style.cursor = tool.cursor;
    this.emitSoon(true);
  }

  setSpawnMode(id: SpawnModeId | string): void {
    const mode = getSpawnMode(id);
    if (mode.id === this.spawnModeId) return;
    this.spawnModeId = mode.id;
    if (this.pointerTool === "spawn") {
      this.setToolFlash("friend", `Spawn · ${mode.label} · ${mode.hint}`, 5000);
    }
    this.emitSoon(true);
  }

  /** Cycle spawn mode (keyboard m / ] ). */
  cycleSpawnMode(dir: 1 | -1 = 1): void {
    const i = SPAWN_MODES.findIndex((m) => m.id === this.spawnModeId);
    const n = SPAWN_MODES.length;
    const next = SPAWN_MODES[((i < 0 ? 0 : i) + dir + n) % n]!;
    this.setSpawnMode(next.id);
  }

  private setToolFlash(kind: ToolFlash["kind"], text: string, ms = 3200): void {
    this.toolFlash = { kind, text, until: performance.now() + ms };
  }

  /** Active flash text if not expired. */
  getToolFlashText(): string | null {
    const f = this.toolFlash;
    if (!f) return null;
    if (performance.now() > f.until) {
      this.toolFlash = null;
      return null;
    }
    return f.text;
  }

  setSelectedIndex(i: number): void {
    const hit = i >= 0 && i < this.sim.n ? i : -1;
    let next: number;
    if (hit < 0) {
      if (this.render.selectedIndex < 0) return;
      next = -1;
    } else if (this.render.selectedIndex === hit) {
      next = -1;
    } else {
      next = hit;
    }
    this.render = { ...this.render, selectedIndex: next };
    this.renderer.draw(this.sim, this.quality.apply(this.render));
    this.emitSoon(true);
  }

  clearSelection(): void {
    if (this.render.selectedIndex < 0 && !this.toolFlash) return;
    this.render = { ...this.render, selectedIndex: -1 };
    this.toolFlash = null;
    this.renderer.draw(this.sim, this.quality.apply(this.render));
    this.emitSoon(true);
  }

  private onVisibility = (): void => {
    this.tabVisible = document.visibilityState === "visible";
    this.audioEngine.setVisible(this.tabVisible);
  };

  /** Sacred Lab/URL audio prefs (clone for panel). */
  get audio(): AudioParams {
    return cloneAudioParams(this.audioState);
  }

  /**
   * Stage gesture unlock when audio is already enabled (URL/teach preset)
   * but context is still locked by autoplay policy.
   */
  private bindStageAudioUnlock(): void {
    if (this.stageUnlockBound) return;
    this.stageUnlockBound = true;
    this.canvas.addEventListener(
      "pointerdown",
      () => {
        if (this.audioState.enabled) void this.audioEngine.unlock();
      },
      { capture: true },
    );
  }

  /**
   * Shared sonification path — tickFrame, stepOnce, tools, disturb.
   * Features from post-mutation SimView + user sacred SimParams (D16).
   */
  private sonifyAfterWork(opts?: {
    dtSec?: number;
    forceExtract?: boolean;
    fullQualityAudio?: boolean;
  }): void {
    const userAudio = this.audioState;
    const tickAudio = this.quality.applyAudio(userAudio, {
      full: opts?.fullQualityAudio === true,
    });
    if (!userAudio.enabled && this.audioEngine.status === "idle") {
      return;
    }
    this.audioEngine.tick(this.sim, this.params, tickAudio, {
      dtSec: opts?.dtSec ?? 0,
      forceExtract: opts?.forceExtract,
    });
  }

  setAudioParams(
    a: AudioParams,
    opts?: { dirty?: boolean; unlockIfEnabled?: boolean },
  ): void {
    const prev = this.audioState;
    this.audioState = validateAudioParams(a);
    if (opts?.dirty !== false) this.dirty = true;
    const allowUnlock = opts?.unlockIfEnabled !== false;
    if (allowUnlock && !prev.enabled && this.audioState.enabled) {
      void this.audioEngine.unlock();
    }
    if (prev.enabled && !this.audioState.enabled) {
      // Engine silences on next tick with enabled=false
    }
    this.scheduleUrl();
    this.emitSoon(true);
  }

  private onKey = (ev: KeyboardEvent): void => {
    const tag = (ev.target as HTMLElement)?.tagName;
    if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;

    if (ev.code === "Space") {
      ev.preventDefault();
      this.togglePlay();
    } else if (ev.key === "Escape") {
      // Clear selection / rewire first; then leave kiosk if still idle
      if ((this.render.selectedIndex ?? -1) >= 0 || this.toolFlash) {
        ev.preventDefault();
        this.clearSelection();
        return;
      }
      if (this.guideModeId === "kiosk") {
        ev.preventDefault();
        this.exitKiosk();
      }
    } else if (ev.key === "." || ev.code === "Period") {
      ev.preventDefault();
      this.stepOnce();
    } else if (ev.key === "r" || ev.key === "R") {
      ev.preventDefault();
      this.reset();
    } else if (ev.key === "s" || ev.key === "S") {
      ev.preventDefault();
      this.share();
    } else if (
      this.pointerTool === "spawn" &&
      (ev.key === "m" || ev.key === "M")
    ) {
      ev.preventDefault();
      this.cycleSpawnMode(1);
    } else if (this.pointerTool === "spawn" && ev.key === "]") {
      ev.preventDefault();
      this.cycleSpawnMode(1);
    } else if (this.pointerTool === "spawn" && ev.key === "[") {
      ev.preventDefault();
      this.cycleSpawnMode(-1);
    } else if (PRESET_HOTKEYS[ev.key]) {
      this.applyPresetId(PRESET_HOTKEYS[ev.key]!);
    }
  };

  private tickFrame(now: number): void {
    const dt =
      this.lastFrameNow > 0
        ? Math.min(0.1, (now - this.lastFrameNow) / 1000)
        : 0;
    this.lastFrameNow = now;

    this.frames++;
    if (now - this.fpsWindow >= 500) {
      this.fps = (this.frames * 1000) / (now - this.fpsWindow);
      this.frames = 0;
      this.fpsWindow = now;
      // Second load signal: sustained fps under ~30 climbs the quality ladder
      // (sim throttle / gravity guard), not only overlay stripping.
      if (this.playing && this.tabVisible) {
        this.quality.noteFps(this.fps);
      }
    }

    // Auto-orbit when dim=3 and not mid-drag (DESIGNS-TRACK-3D)
    let cameraMoved = false;
    if (
      this.sim.params.dim >= 3 &&
      this.tabVisible &&
      !this.orbit &&
      (this.render.autoRotate ?? 0) !== 0 &&
      dt > 0
    ) {
      this.render = {
        ...this.render,
        cameraYaw: this.render.cameraYaw + this.render.autoRotate * dt,
      };
      cameraMoved = true;
    }

    const shouldStep = this.playing && this.tabVisible;
    let didStep = false;
    if (shouldStep) {
      // Q4/Q5 scale down steps-per-frame so expensive gravity/n combos cannot
      // lock the main thread. User's Lab speed setting is unchanged.
      this.stepCredit += this.stepsPerFrame * this.quality.stepScale();
      const nSteps = Math.floor(this.stepCredit);
      if (nSteps >= 1) {
        this.stepCredit -= nSteps;
        const t0 = performance.now();
        // Working-copy sim params only (mutual→COM, gravity off, …). Restored
        // after the batch so Lab / URL prefs never stick at the degraded mode.
        const userParams = this.sim.params;
        const stepParams = this.quality.applySim(userParams);
        if (stepParams !== userParams) this.sim.params = stepParams;
        try {
          this.sim.step(nSteps);
        } finally {
          if (stepParams !== userParams) this.sim.params = userParams;
        }
        this.lastStepMs = performance.now() - t0;
        // Per-step timing; coalesces sub-timer-quantum batches inside StepTimingStats
        this.stepStats.pushMs(this.lastStepMs, nSteps);
        if (this.choreo?.active) {
          for (let i = 0; i < nSteps; i++) this.choreo.tick();
        }
        didStep = true;
      }
    }

    // Live playback / orbit always redraws. When paused and static, draw once
    // at full quality — never let adaptive quality strip edges frame-to-frame
    // (Frozen Graph pause strobe).
    const live = shouldStep || cameraMoved || !!this.orbit;
    if (live || this.needsDraw) {
      const drawParams = live
        ? this.quality.apply(this.render)
        : this.quality.apply(this.render, { full: true });
      this.renderer.draw(this.sim, drawParams);
      const drawMs = this.renderer.lastDrawMs ?? 0;
      this.sonifyAfterWork({ dtSec: dt });
      const audioMs = this.audioEngine.lastTickMs;
      if (live) {
        this.quality.noteFrameCost(this.lastStepMs + drawMs + audioMs);
      } else {
        // Paused static frame: don't train quality on a one-shot full draw
        this.quality.cool();
      }
      this.needsDraw = false;
    } else {
      // No draw this frame — still sonify if we stepped or audio is live
      if (didStep || this.audioState.enabled) {
        this.sonifyAfterWork({ dtSec: dt });
        if (live) {
          this.quality.noteFrameCost(
            this.lastStepMs + this.audioEngine.lastTickMs,
          );
        }
      }
    }

    // HUD / panel at ~8 Hz max — avoids 60 DOM writes/s
    if (this.needsEmit || now - this.lastHudEmit >= 120) {
      this.needsEmit = false;
      this.lastHudEmit = now;
      this.emit();
    }
  }

  snapshot(): AppStateV1 {
    return {
      v: 1,
      preset: this.presetId,
      dirty: this.dirty,
      playing: this.playing,
      stepsPerFrame: this.stepsPerFrame,
      guide: this.guideModeId,
      params: cloneSimParams(this.sim.params),
      render: cloneRenderParams(this.render),
      audio: cloneAudioParams(this.audioState),
    };
  }

  /** Debounced URL persistence (immediate optional). */
  scheduleUrl(immediate = false): void {
    if (this.urlTimer) clearTimeout(this.urlTimer);
    if (immediate) {
      writeUrl(this.snapshot());
      return;
    }
    this.urlTimer = setTimeout(() => {
      writeUrl(this.snapshot());
      this.urlTimer = null;
    }, 300);
  }

  setSimParams(
    params: SimParams,
    opts?: { reset?: boolean; dirty?: boolean },
  ): void {
    const dirty = opts?.dirty !== false;
    if (dirty) this.dirty = true;
    // Init-only knobs (gridExtent/Jitter) require full re-seed of positions — Track Z1 / C3
    const needReset =
      opts?.reset ||
      params.n !== this.sim.params.n ||
      params.dim !== this.sim.params.dim ||
      params.topology !== this.sim.params.topology ||
      params.initPositions !== this.sim.params.initPositions ||
      params.ringStride !== this.sim.params.ringStride ||
      params.model !== this.sim.params.model ||
      params.gridExtent !== this.sim.params.gridExtent ||
      params.gridJitter !== this.sim.params.gridJitter ||
      params.typeCount !== this.sim.params.typeCount ||
      params.relationProfile !== this.sim.params.relationProfile;
    if (needReset) {
      this.sim = new Simulation(params);
    } else {
      this.sim.setParams(params);
    }
    this.scheduleUrl();
    this.emitSoon(true);
  }

  setRender(render: RenderParams, opts?: { dirty?: boolean }): void {
    if (opts?.dirty !== false) this.dirty = true;
    this.render = cloneRenderParams(render);
    this.scheduleUrl();
    this.emitSoon(true);
  }

  /**
   * Single entry for schema-driven control edits.
   * Path: `sim.*` | `render.*` | `audio.*`. Owns freeze↔rechoose
   * coupling so the panel never mutates live params.
   */
  patchControl(
    path: string,
    value: unknown,
    opts?: { reset?: boolean; dirty?: boolean },
  ): void {
    const result = applyControlPatch(
      this.params,
      this.render,
      this.audioState,
      path,
      value,
    );
    if (result.kind === "sim") {
      this.setSimParams(result.params, opts);
    } else if (result.kind === "render") {
      this.setRender(result.render, { dirty: opts?.dirty });
    } else if (result.kind === "audio") {
      // Track which audio keys the user touched (enable-edge heuristics).
      const leaf = path.split(".").slice(1).join(".");
      if (leaf) this.audioExplicit.add(leaf === "enabled" ? "enabled" : leaf);
      // Also mark nested-style single keys from schema (enabled, masterGain, …)
      const key = path.replace(/^audio\./, "");
      if (key) this.audioExplicit.add(key);

      let next = result.audio;
      const enabling = !this.audioState.enabled && next.enabled;
      if (enabling) {
        next = resolveAudioDefaults(next, {
          explicit: this.audioExplicit,
          sim: this.params,
        });
      }
      this.setAudioParams(next, { dirty: opts?.dirty });
    }
  }

  applyPreset(preset: Preset): void {
    this.presetId = preset.id;
    this.dirty = false;
    this.sim = new Simulation(cloneSimParams(preset.params));
    this.sim.step(20);
    this.render = {
      ...cloneRenderParams(preset.render),
      selectedIndex: -1,
    };
    // Teach presets may enable audio; gesture-safe unlock (user clicked preset)
    let audio = cloneAudioParams(preset.audio ?? defaultAudioParams());
    if (audio.enabled) {
      audio = resolveAudioDefaults(audio, {
        explicit: new Set(
          Object.keys(audio).filter(
            (k) =>
              (audio as Record<string, unknown>)[k] !==
              (defaultAudioParams() as Record<string, unknown>)[k],
          ),
        ),
        sim: preset.params,
      });
    }
    this.audioExplicit = new Set();
    this.setAudioParams(audio, { dirty: false, unlockIfEnabled: true });
    this.renderer.clear(this.render.background);
    this.sonifyAfterWork({ forceExtract: true, fullQualityAudio: true });
    this.scheduleUrl(true);
    this.emitSoon(true);
  }

  applyPresetId(id: string): void {
    this.applyPreset(getPreset(id));
  }

  togglePlay(): void {
    this.playing = !this.playing;
    this.needsDraw = true;
    this.audioEngine.setPlaying(this.playing);
    if (!this.playing) this.quality.cool();
    this.scheduleUrl();
    this.emitSoon(true);
  }

  setPlaying(playing: boolean): void {
    this.playing = playing;
    this.needsDraw = true;
    this.audioEngine.setPlaying(this.playing);
    if (!playing) this.quality.cool();
    this.scheduleUrl();
    this.emitSoon(true);
  }

  stepOnce(): void {
    const t0 = performance.now();
    this.sim.step(1);
    this.lastStepMs = performance.now() - t0;
    this.stepStats.pushMs(this.lastStepMs, 1);
    this.renderer.draw(this.sim, this.quality.apply(this.render, { full: true }));
    this.sonifyAfterWork({ forceExtract: true, fullQualityAudio: true });
    this.emitSoon(true);
  }

  reset(): void {
    this.sim.reset();
    this.renderer.clear(this.render.background);
    this.renderer.draw(this.sim, this.quality.apply(this.render, { full: true }));
    this.sonifyAfterWork({ forceExtract: true, fullQualityAudio: true });
    this.emitSoon(true);
  }

  reseed(): void {
    this.sim.reseed();
    this.dirty = true;
    this.renderer.clear(this.render.background);
    this.renderer.draw(this.sim, this.quality.apply(this.render, { full: true }));
    this.sonifyAfterWork({ forceExtract: true, fullQualityAudio: true });
    this.scheduleUrl();
    this.emitSoon(true);
  }

  setStepsPerFrame(n: number): void {
    this.stepsPerFrame = normalizeSpeed(n);
    this.stepCredit = 0;
    this.scheduleUrl();
    this.emitSoon(true);
  }

  setGuideMode(id: string): void {
    const next = getGuideMode(id).id;
    if (next === "kiosk") {
      this.enterKiosk({ applyOverlay: !this.dirty });
      return;
    }
    if (this.guideModeId === "kiosk" && next !== "kiosk") {
      // Leaving kiosk via dropdown — restore snapshot if present
      if (this.preKiosk) {
        this.render = cloneRenderParams(this.preKiosk.render);
        this.preKiosk = null;
      }
    }
    this.guideModeId = next;
    applyGuideChrome(getGuideMode(next));
    this.scheduleUrl();
    this.emitSoon(true);
  }

  /** Open copy-link dialog with the current exhibit URL. */
  share(): void {
    this.scheduleUrl(true);
    const url = prepareShareUrl(this.snapshot());
    getShareModal().open({ url, title: "Copy link" });
  }

  toggleRecord(): void {
    if (this.recorder.recording) {
      this.recorder.stop();
    } else {
      try {
        this.recorder.start(this.canvas, 30);
      } catch (err) {
        console.warn(err);
      }
    }
    this.emitSoon(true);
  }

  /** One-shot choreography: rewire a random friend edge. */
  disturbRandomFriend(): void {
    this.sim.disturbRandomFriend();
    this.dirty = true;
    // No draw on this path — still sonify topology chorus (D25)
    this.sonifyAfterWork({ forceExtract: true, fullQualityAudio: true });
    this.scheduleUrl();
    this.emitSoon(true);
  }

  /**
   * Load a built-in script and start it (select = run / Replay).
   * Immediate ops apply now; timed waits need playback — auto-plays if needed.
   * Selection stays sticky until the user picks “Script…”.
   */
  runChoreographyScript(id: string): void {
    const script = getChoreographyScript(id);
    if (!script) return;
    // Re-bind target in case sim instance was replaced
    this.choreo = new ChoreographyPlayer(simulationTarget(this.sim));
    this.choreo.load(script.ops, { id: script.id, label: script.label });
    this.choreo.pumpImmediate();
    if (this.choreo.active && !this.playing) {
      this.playing = true;
    }
    this.dirty = true;
    this.scheduleUrl();
    this.emitSoon(true);
  }

  /** Forget selection and abort (user picked “Script…”). */
  clearChoreography(): void {
    this.choreo?.clear();
    this.emitSoon(true);
  }

  /** Abort a running script but keep the selection as done. */
  stopChoreography(): void {
    this.choreo?.stop();
    this.emitSoon(true);
  }

  get choreographyActive(): boolean {
    return !!this.choreo?.active;
  }

  get choreographyStatus(): "idle" | "running" | "done" {
    return this.choreo?.status ?? "idle";
  }

  get choreographyId(): string | null {
    return this.choreo?.scriptId ?? null;
  }

  get choreographyLabel(): string | null {
    return this.choreo?.scriptLabel ?? null;
  }

  listChoreographyScripts(): ChoreographyScript[] {
    return BUILTIN_SCRIPTS.slice();
  }

  /**
   * Load-guard report for HUD / stage chip.
   * Prefs are never rewritten — only temporary per-frame backoffs are listed.
   */
  qualityReport() {
    return this.quality.report(this.sim.params, this.render, {
      stepsPerFrame: this.stepsPerFrame,
      fps: this.fps,
      userAudio: this.audioState,
    });
  }

  /** Short HUD line (ellipsis-safe). Full detail via perfTitle(). */
  perfText(): string {
    const agg = this.stepStats.aggregates();
    const stepPart = agg ? formatStepAggregates(agg) : "step —";
    const q = this.qualityReport();
    return (
      `${this.fps.toFixed(0)} fps · n ${this.sim.params.n} · ` +
      `${stepPart} · t ${this.sim.tick}` +
      q.shortLabel
    );
  }

  /** Full timing string for the HUD title tooltip (includes load-guard detail). */
  perfTitle(): string {
    const agg = this.stepStats.aggregates();
    const stepPart = agg ? formatStepAggregatesFull(agg) : "step —";
    let scriptPart = "";
    if (this.choreo?.scriptLabel) {
      const st = this.choreo.status;
      if (st === "running")
        scriptPart = ` · script “${this.choreo.scriptLabel}”…`;
      else if (st === "done")
        scriptPart = ` · script “${this.choreo.scriptLabel}” ✓`;
    }
    const q = this.qualityReport();
    const base =
      `${this.fps.toFixed(0)} fps · n ${this.sim.params.n} · ` +
      `${stepPart} · t ${this.sim.tick}` +
      scriptPart;
    return q.active ? `${base}\n${q.detail}` : base;
  }
}
